import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Search, Loader2, Sparkles, Send, Lightbulb, GraduationCap, CheckCircle2, AlertCircle, User, History, Star, MapPin, Clock, Trash2 } from "lucide-react";
import { SwishPricing } from "@/components/SwishPricing";

function MatchBar({ label, value, tone }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold">{v}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${tone === "accent" ? "bg-accent" : "bg-primary"}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

function MatchCard({ m, searchId, searchTitle, contacted, onContacted, saved, onToggleSave }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const already = contacted.includes(m.candidate_id);
  const isSaved = saved.includes(m.candidate_id);

  const sendRequest = async () => {
    setSending(true);
    try {
      await api.post("/employer/contact-requests", { candidate_id: m.candidate_id, search_id: searchId, message });
      toast.success("Kontaktförfrågan skickad");
      setOpen(false);
      onContacted(m.candidate_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Card className="rounded-2xl border-border p-5" data-testid={`match-${m.candidate_id}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><User className="h-4.5 w-4.5" /></div>
          <div className="font-mono text-sm font-semibold text-primary">{m.candidate_id}</div>
        </div>
        <Button size="sm" variant={isSaved ? "default" : "outline"} className="rounded-full" onClick={() => onToggleSave(m, searchId, searchTitle)} data-testid={`save-btn-${m.candidate_id}`}>
          <Star className={`h-4 w-4 ${isSaved ? "fill-current" : ""} ${isSaved ? "" : "mr-1.5"}`} />{isSaved ? "" : "Spara"}
        </Button>
      </div>
      {(m.location || m.extent) && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`match-prefs-${m.candidate_id}`}>
          {m.location && <Badge variant="secondary" className="rounded-full gap-1"><MapPin className="h-3 w-3" /> {m.location}</Badge>}
          {m.extent && <Badge variant="secondary" className="rounded-full gap-1"><Clock className="h-3 w-3" /> {m.extent}</Badge>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 mb-3">
        <MatchBar label="Rollmatchning" value={m.role_match} />
        <MatchBar label="Karaktärsmatchning" value={m.character_match} tone="accent" />
      </div>
      {m.verdict && <p className="text-sm text-muted-foreground mb-3">{m.verdict}</p>}
      {(m.strengths || []).length > 0 && (
        <div className="mb-2">
          {m.strengths.map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> <span>{s}</span></div>
          ))}
        </div>
      )}
      {(m.gaps || []).length > 0 && (
        <div className="mb-2">
          {m.gaps.map((g, i) => (
            <div key={i} className="flex items-start gap-1.5 text-sm text-muted-foreground"><AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> <span>{g}</span></div>
          ))}
        </div>
      )}
      {(m.tips || []).length > 0 && (
        <div className="rounded-xl bg-primary/[0.05] border border-primary/15 p-3 mb-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-primary mb-1"><Lightbulb className="h-4 w-4" /> Tips</div>
          {m.tips.map((t, i) => <div key={i} className="text-sm text-foreground/80">• {t}</div>)}
        </div>
      )}
      {(m.education_suggestions || []).length > 0 && (
        <div className="rounded-xl bg-accent/[0.07] border border-accent/20 p-3 mb-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-accent mb-1"><GraduationCap className="h-4 w-4" /> Utbildningar som stärker matchningen</div>
          {m.education_suggestions.map((e, i) => <div key={i} className="text-sm text-foreground/80">• {e}</div>)}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="rounded-full" disabled={already} data-testid={`contact-btn-${m.candidate_id}`}>
            {already ? <>Förfrågan skickad</> : <><Send className="h-4 w-4 mr-1.5" /> Skicka kontaktförfrågan</>}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Kontakta {m.candidate_id}</DialogTitle></DialogHeader>
          <DialogDescription>Kandidaten ser bara ditt företagsnamn och ditt meddelande – och väljer själv om hen vill svara. Namn/kontaktuppgifter delas aldrig utan kandidatens godkännande.</DialogDescription>
          <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Skriv en kort hälsning och varför ni är intresserade…" data-testid="contact-message-input" />
          <DialogFooter>
            <Button className="rounded-full" onClick={sendRequest} disabled={sending} data-testid="contact-send-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Skicka förfrågan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function ForetagSok() {
  const [form, setForm] = useState({ title: "", role_description: "", desired_person: "", location: "", extent: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [contacted, setContacted] = useState([]);
  const [saved, setSaved] = useState([]);

  const loadHistory = () => api.get("/employer/searches").then((r) => setHistory(r.data)).catch(() => {});
  const loadContacted = () => api.get("/employer/contact-requests").then((r) => setContacted((r.data || []).filter((c) => c.status !== "declined").map((c) => c.candidate_id))).catch(() => {});
  const loadSaved = () => api.get("/employer/shortlist").then((r) => setSaved((r.data || []).map((s) => s.candidate_id))).catch(() => {});
  useEffect(() => { loadHistory(); loadContacted(); loadSaved(); }, []);

  const toggleSave = async (m, searchId, searchTitle) => {
    const isSaved = saved.includes(m.candidate_id);
    try {
      if (isSaved) {
        await api.delete(`/employer/shortlist/${m.candidate_id}`);
        setSaved((s) => s.filter((c) => c !== m.candidate_id));
        toast.success("Borttagen från sparade");
      } else {
        await api.post("/employer/shortlist", { candidate_id: m.candidate_id, match: m, search_id: searchId, role_title: searchTitle });
        setSaved((s) => [...s, m.candidate_id]);
        toast.success("Sparad till din lista");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const run = async () => {
    if (!form.title.trim() || !form.role_description.trim()) { toast.error("Fyll i tjänstetitel och rollbeskrivning"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/employer/searches", form);
      setResult(data);
      loadHistory();
      if ((data.matches || []).length === 0 && data.candidate_count === 0) toast.info("Inga kandidater är tillgängliga just nu.");
      else toast.success("Matchning klar");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openHistory = async (id) => {
    try { const { data } = await api.get(`/employer/searches/${id}`); setResult(data); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteHistory = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Vill du radera den här sökningen?")) return;
    try {
      await api.delete(`/employer/searches/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      setResult((r) => (r && r.id === id ? null : r));
      toast.success("Sökningen raderad");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-head text-3xl font-bold flex items-center gap-2"><Search className="h-7 w-7 text-primary" /> Sök kandidater</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Beskriv tjänsten och vilken person ni söker. Verktyget matchar tillgängliga kandidater på både roll och karaktär – anonymt, endast HAMN-id. Matchningen är ett beslutsunderlag, aldrig ett facit.</p>
      </div>

      <div className="mb-6"><SwishPricing audience="employer" /></div>

      <Card className="rounded-2xl border-border p-5 md:p-6 mb-6" data-testid="employer-search-form">
        <div className="grid gap-4">
          <div>
            <Label className="text-sm">Tjänst / titel</Label>
            <Input className="mt-1.5 h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="T.ex. Kock, Lagerarbetare, Ekonomiassistent" data-testid="search-title" />
          </div>
          <div>
            <Label className="text-sm">Rollbeskrivning</Label>
            <Textarea className="mt-1.5" rows={5} value={form.role_description} onChange={(e) => setForm({ ...form, role_description: e.target.value })} placeholder="Beskriv arbetsuppgifter, krav och önskad erfarenhet…" data-testid="search-role" />
          </div>
          <div>
            <Label className="text-sm">Vilken typ av person söker ni? (karaktär)</Label>
            <Textarea className="mt-1.5" rows={3} value={form.desired_person} onChange={(e) => setForm({ ...form, desired_person: e.target.value })} placeholder="T.ex. lugn under press, ansvarstagande, lagspelare, initiativrik…" data-testid="search-person" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm">Ort (valfritt)</Label>
              <Input className="mt-1.5 h-11" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="T.ex. Malmö" data-testid="search-location" />
            </div>
            <div>
              <Label className="text-sm">Omfattning (valfritt)</Label>
              <div className="mt-1.5 flex gap-2" data-testid="search-extent">
                {[["", "Alla"], ["heltid", "Heltid"], ["deltid", "Deltid"]].map(([val, lbl]) => (
                  <Button key={val || "alla"} type="button" variant={form.extent === val ? "default" : "outline"} className="rounded-full flex-1"
                    onClick={() => setForm({ ...form, extent: val })} data-testid={`extent-${val || "alla"}`}>{lbl}</Button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">Kandidater som inte angett ort eller omfattning tas med som flexibla.</p>
          <div>
            <Button className="rounded-full" onClick={run} disabled={loading} data-testid="run-search-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Matcha kandidater
            </Button>
          </div>
        </div>
      </Card>

      {result && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head font-semibold text-lg">Matchningar för "{result.title}"</h2>
            <Badge variant="secondary" className="rounded-full">{result.candidate_count} tillgängliga kandidater</Badge>
          </div>
          {(result.matches || []).length === 0 ? (
            <Card className="rounded-2xl border-dashed border-border p-8 text-center" data-testid="no-matches">
              <p className="text-sm text-muted-foreground">Inga matchande kandidater hittades just nu. Prova att bredda rollbeskrivningen eller kom tillbaka senare – fler kandidater gör sig tillgängliga löpande.</p>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="match-results">
              {result.matches.map((m) => (
                <MatchCard key={m.candidate_id} m={m} searchId={result.id} searchTitle={result.title} contacted={contacted} onContacted={(cid) => setContacted((c) => [...c, cid])} saved={saved} onToggleSave={toggleSave} />
              ))}
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground mb-3"><History className="h-4 w-4" /> Tidigare sökningar</div>
          <div className="grid gap-2">
            {history.map((h) => (
              <Card key={h.id} className="rounded-xl border-border p-3 flex items-center justify-between gap-3 cursor-pointer hover:border-primary/40" onClick={() => openHistory(h.id)} data-testid={`history-${h.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{h.title}</div>
                  <div className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString("sv-SE")} · {(h.matches || []).length} matchningar</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="rounded-full">Öppna</Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground hover:text-destructive" onClick={(e) => deleteHistory(h.id, e)} data-testid={`delete-history-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
